import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { StatusEnum } from "../../../../common/data/base/enums/statusEnum";
import MESSAGES from "../../../../common/data/constants/messages";
import { ISurvey } from "../../../../common/data/dtos/survey";
import { ISurveyQuestion } from "../../../../common/data/dtos/survey-question";
import { ISurveyQuestionItem } from "../../../../common/data/dtos/survey-question-item";
import SurveyQuestionItemTypeEnum from "../../../../common/data/enums/enumSurveyQuestionItemType";
import Badge from "../../../../components/bootstrap/Badge";
import Button from "../../../../components/bootstrap/Button";
import Card, { CardFooterRight, CardTabItem } from "../../../../components/bootstrap/Card";
import FormGroup from "../../../../components/bootstrap/forms/FormGroup";
import Input from "../../../../components/bootstrap/forms/Input";
import InputGroup from "../../../../components/bootstrap/forms/InputGroup";
import Select from "../../../../components/bootstrap/forms/Select";
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from "../../../../components/bootstrap/Modal";
import Spinner from "../../../../components/bootstrap/Spinner";
import { showSuccess, showWarning } from "../../../../components/extras/Notifications";
import { generateTempId } from "../../../../helpers/helpers";
import surveyQuestionItemService from "../../../../services/survey-question-item-service";

type SurveyQuestionFormValues = {
    id?: string;
    question: string;
    type: string;
};

interface Props {
    isModalOpened: boolean;
    selectedSurvey: ISurvey | null;
    selectedSurveyQuestion: ISurveyQuestion | null;
    onModalClose: () => void;
    onAfterSubmit: () => void;
}

export const SurveyQuestionItemModal = ({ isModalOpened, selectedSurvey, selectedSurveyQuestion, onModalClose, onAfterSubmit }: Props) => {

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [surveyQuestionItems, setSurveyQuestionItems] = useState<ISurveyQuestionItem[]>([]);

    const formik = useFormik({
        initialValues: {
            question: '',
            type: '1'
        },

        onSubmit: (values, { resetForm }) => {
            handleAddClick(values);
            resetForm();
        },
    });

    useEffect(() => {
        resetForm();

        if (selectedSurveyQuestion) {
            fetchSurveyQuestionItems();
        }

    }, [isModalOpened]);

    const fetchSurveyQuestionItems = async () => {

        setIsSubmitting(true);

        if (!selectedSurveyQuestion) {
            showWarning(MESSAGES.VALIDATION.PARAMETERS_ERROR_VALIDATION);
            setIsSubmitting(false);
        } else {
            const response = await surveyQuestionItemService.getSurveyQuestionItemBySurveyQuestion(selectedSurveyQuestion.id!);

            setIsSubmitting(false);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            setSurveyQuestionItems(response.surveyQuestionItems || []);
        }
    };

    const onFormSubmit = async (values: SurveyQuestionFormValues) => {
        if (!surveyQuestionItems) {
            showWarning(MESSAGES.VALIDATION.EMPTY_COLLECTION)
            return;
        }

        const itensToAdd = surveyQuestionItems
            .filter(item => !item.id || item.id.trim() === '');


        if (!itensToAdd || itensToAdd.length === 0) {
            showWarning(MESSAGES.VALIDATION.EMPTY_COLLECTION_TO_ADD)
            return;
        }

        setIsSubmitting(true);

        const response = await surveyQuestionItemService.createManySurveyQuestionItem(itensToAdd);

        setIsSubmitting(false);

        if (response.error) {
            showWarning(MESSAGES.ACTION.SAVE_ERROR);
            return;
        }

        showSuccess(MESSAGES.ACTION.SAVED);
        resetForm()
        onAfterSubmit();
    };

    const onClose = () => {
        onModalClose();
    };

    const resetForm = () => {
        formik.resetForm({
            values: {
                question: '',
                type: '1'
            },
        });
    };

    const handleAddClick = async (values: SurveyQuestionFormValues) => {

        if (!values.question || !selectedSurveyQuestion || !values.type) {
            showWarning(MESSAGES.VALIDATION.PARAMETERS_ERROR_VALIDATION);
            return;
        }

        const newSurveyQuestionItem: ISurveyQuestionItem = {
            question: values.question,
            type: values.type === '0' ? SurveyQuestionItemTypeEnum.descriptive : SurveyQuestionItemTypeEnum.alternative,
            surveyQuestionId: selectedSurveyQuestion.id!,
            rowVersion: 1,
            status: StatusEnum.Active,
            tempId: generateTempId()
        };
        setSurveyQuestionItems((prev) => [
            ...prev,
            newSurveyQuestionItem,
        ]);
    }

    const handleDeleteClick = async (surveyQuestionItem: ISurveyQuestionItem) => {
        if (!surveyQuestionItem.id) {
            const filteredList = surveyQuestionItems.filter(item => item.tempId !== surveyQuestionItem.tempId);
            setSurveyQuestionItems(filteredList);
        } else {

            setIsSubmitting(true);

            const response = await surveyQuestionItemService.deleteSurveyQuestionItem(surveyQuestionItem.id);

            setIsSubmitting(false);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            showSuccess(MESSAGES.ACTION.DELETED);
            fetchSurveyQuestionItems();
        }
    }

    return (
        <Modal isOpen={isModalOpened} setIsOpen={onClose} size={'xl'} isCentered isStaticBackdrop>
            <ModalHeader setIsOpen={onClose}>
                <ModalTitle id='survey-header-item-modal'>Manutenção de itens das questões</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <Card hasTab tabButtonColor='primary'>
                    <CardTabItem id='tab-item-1' title='Dados dos itens das questões' icon='ViewHeadline'>
                        <div className='row g-4'>

                            <div className='col-lg-6 col-md-6 col-sm-12'>

                                <FormGroup id='question' label='Item da Questão' isFloating>
                                    <Input
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.question}
                                    />
                                </FormGroup>
                            </div>

                            <div className='col-lg-6 col-md-6 col-sm-12'>
                                <InputGroup
                                    isWrap={true}
                                >
                                    <FormGroup
                                        id='type'
                                        label='Tipo'
                                        isFloating
                                    >
                                        <Select
                                            ariaLabel='Escolha um tipo'
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.type}
                                            list={
                                                [
                                                    { value: '0', text: 'Descritiva' },
                                                    { value: '1', text: 'Alternativa' },
                                                ]
                                            }
                                        />
                                    </FormGroup>
                                    <Button
                                        icon='AddCircleOutline'
                                        color='primary'
                                        onClick={formik.handleSubmit}
                                    >
                                        Adicionar
                                    </Button>
                                </InputGroup>
                            </div>


                            <div className='col-lg-12 col-md-12 col-sm-12 table-responsive '>

                                <table className='table table-modern table-hover'>
                                    <thead>
                                        <tr>
                                            <th>Questão</th>
                                            <th>Tipo</th>
                                            <td aria-labelledby='Actions' />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {surveyQuestionItems
                                            .map((item: ISurveyQuestionItem) => (
                                                <tr key={item.id}>
                                                    <td>{item.question}</td>

                                                    <td>
                                                        {item.type === SurveyQuestionItemTypeEnum.descriptive ? (
                                                            <h5>
                                                                <Badge color='info' className='align-items-center justify-content-center'>
                                                                    Descritiva
                                                                </Badge>
                                                            </h5>
                                                        ) : (
                                                            <h5>
                                                                <Badge color='info' className='align-items-center justify-content-center'>
                                                                    Alternativa
                                                                </Badge>
                                                            </h5>
                                                        )}
                                                    </td>

                                                    <td className='text-end'>
                                                        <Button
                                                            color='dark'
                                                            isLight
                                                            icon='Delete'
                                                            onClick={() => handleDeleteClick(item)}
                                                            aria-label='Remover'
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>

                        </div>
                    </CardTabItem>
                </Card>
            </ModalBody>
            <ModalFooter className='bg-transparent'>
                <CardFooterRight>
                    <Button color='success' className='w-100' onClick={onFormSubmit} isDisable={isSubmitting}>
                        {isSubmitting && <Spinner isSmall inButton isGrow />}
                        Salvar
                    </Button>
                </CardFooterRight>
            </ModalFooter>
        </Modal >
    );
};